import { useState, useRef, useCallback, type ReactNode } from 'react';
import { useMatch } from 'react-router-dom';
import { useUploadStore } from '../../stores/upload';
import { useAuthStore } from '../../stores/auth';
import { toast } from 'sonner';
import { Upload, ImagePlus } from 'lucide-react';

interface GlobalDropZoneProps {
  children: ReactNode;
}

export function GlobalDropZone({ children }: GlobalDropZoneProps) {
  const user = useAuthStore(s => s.user);
  const [isDragging, setIsDragging] = useState(false);
  const [hoveredZone, setHoveredZone] = useState<'left' | 'right' | null>(null);
  const dragCounter = useRef(0);
  const { uploadFiles, uploadFilesToAsset } = useUploadStore();
  const assetMatch = useMatch('/assets/:id');

  const canUpload = user?.role === 'Admin' || user?.role === 'Editor' || user?.role === 'Photographer';

  const handleDragEnter = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current++;
    if (e.dataTransfer.types.includes('Files')) {
      setIsDragging(true);
    }
  }, []);

  const handleDragLeave = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
    dragCounter.current--;
    if (dragCounter.current === 0) {
      setIsDragging(false);
      setHoveredZone(null);
    }
  }, []);

  const handleDragOver = useCallback((e: React.DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  }, []);

  const handleDrop = useCallback(
    (e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);
      setHoveredZone(null);

      if (!canUpload) {
        toast.error('You do not have permission to upload assets');
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      uploadFiles(files);
    },
    [canUpload, uploadFiles]
  );

  const handleZoneDrop = useCallback(
    (zone: 'left' | 'right', e: React.DragEvent) => {
      e.preventDefault();
      e.stopPropagation();
      dragCounter.current = 0;
      setIsDragging(false);
      setHoveredZone(null);

      if (!canUpload) {
        toast.error('You do not have permission to upload assets');
        return;
      }

      const files = Array.from(e.dataTransfer.files);
      if (zone === 'left' && assetMatch?.params.id) {
        uploadFilesToAsset(parseInt(assetMatch.params.id, 10), files);
      } else {
        uploadFiles(files);
      }
    },
    [canUpload, uploadFiles, uploadFilesToAsset, assetMatch]
  );

  if (!canUpload) {
    return <>{children}</>;
  }

  const isOnAssetPage = !!assetMatch?.params.id;

  return (
    <div
      className="relative h-full w-full"
      onDragEnter={handleDragEnter}
      onDragLeave={handleDragLeave}
      onDragOver={handleDragOver}
      onDrop={isOnAssetPage ? undefined : handleDrop}
    >
      {children}

      {isDragging && !isOnAssetPage && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-background/80 backdrop-blur-sm">
          <div className="flex flex-col items-center gap-4 rounded-xl border-2 border-dashed border-primary bg-primary/5 p-12">
            <Upload className="h-12 w-12 text-primary" />
            <div className="text-center">
              <p className="text-lg font-semibold">Drop images to upload</p>
              <p className="text-sm text-muted-foreground">
                Photos will be matched to lots by EXIF timestamp
              </p>
            </div>
          </div>
        </div>
      )}

      {isDragging && isOnAssetPage && (
        <div className="fixed inset-0 z-50 flex h-full bg-background/80 backdrop-blur-sm">
          <div
            className={`flex flex-1 flex-col items-center justify-center gap-4 border-r-2 border-dashed transition-colors ${
              hoveredZone === 'left'
                ? 'border-primary bg-primary/10'
                : 'border-muted bg-muted/5'
            }`}
            onDragEnter={() => setHoveredZone('left')}
            onDragLeave={() => setHoveredZone(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleZoneDrop('left', e)}
          >
            <ImagePlus className={`pointer-events-none h-12 w-12 ${hoveredZone === 'left' ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="pointer-events-none text-center">
              <p className="text-lg font-semibold">Add to this asset</p>
              <p className="text-sm text-muted-foreground">
                Photos will be attached directly
              </p>
            </div>
          </div>

          <div
            className={`flex flex-1 flex-col items-center justify-center gap-4 border-l-2 border-dashed transition-colors ${
              hoveredZone === 'right'
                ? 'border-primary bg-primary/10'
                : 'border-muted bg-muted/5'
            }`}
            onDragEnter={() => setHoveredZone('right')}
            onDragLeave={() => setHoveredZone(null)}
            onDragOver={(e) => e.preventDefault()}
            onDrop={(e) => handleZoneDrop('right', e)}
          >
            <Upload className={`pointer-events-none h-12 w-12 ${hoveredZone === 'right' ? 'text-primary' : 'text-muted-foreground'}`} />
            <div className="pointer-events-none text-center">
              <p className="text-lg font-semibold">Match by QR / Timeline</p>
              <p className="text-sm text-muted-foreground">
                Photos will be matched to lots automatically
              </p>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
